import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import {
  Plus,
  Search,
  Package,
  Edit,
  AlertTriangle,
  TrendingUp,
  Eye,
  Filter,
  MapPin,
  DollarSign,
  Boxes,
  LayoutGrid,
  Columns,
} from "lucide-react";
import { MaterialInventoryFormDialog } from "@/components/Inventory/MaterialInventoryFormDialog";
import { MaterialDetails } from "@/components/Inventory/MaterialDetails";
import { MaterialKanbanView } from "@/components/Inventory/MaterialKanbanView";
import { cn } from "@/lib/utils";
import type { MaterialInventory } from "@/types/legacy";

interface MaterialInventoryPageProps {
  embedded?: boolean;
}

export default function MaterialInventoryPage({ embedded = false }: MaterialInventoryPageProps) {
  const { userRole } = useAuth();
  const queryClient = useQueryClient();
  const canSeeCosts = ['admin', 'supervisor', 'plotter'].includes(userRole || '');
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialInventory | null>(null);
  const [viewMaterialId, setViewMaterialId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "kanban">("grid");

  const { data: materials, isLoading } = useQuery({
    queryKey: ["material-inventory"],
    queryFn: async () => {
      const { data, error } = await api
        .from("material_inventory")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as MaterialInventory[];
    },
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  // Fetch material lots for accurate value calculation
  const { data: materialLots } = useQuery({
    queryKey: ["material-lots-all"],
    queryFn: async () => {
      const { data, error } = await api
        .from("material_lots")
        .select("material_id, square_meters")
        .eq("is_active", true);
      if (error) throw error;
      return data as { material_id: string; square_meters: number }[];
    },
    refetchInterval: 5000,
  });

  const categories = [...new Set(materials?.map((m) => m.category).filter(Boolean) || [])];

  const filteredMaterials = materials?.filter((material) => {
    const matchesSearch =
      searchTerm === "" ||
      material.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === "all" || material.category === categoryFilter;
    const isLowStock = material.current_stock < material.min_stock;
    const matchesStock =
      stockFilter === "all" ||
      (stockFilter === "low" && isLowStock) ||
      (stockFilter === "normal" && !isLowStock);
    return matchesSearch && matchesCategory && matchesStock;
  });

  const handleEdit = (material: MaterialInventory, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedMaterial(material);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedMaterial(null);
    setDialogOpen(true);
  };

  const handleViewDetails = (materialId: string) => {
    setViewMaterialId(materialId);
  };

  const canManage = userRole === "admin" || userRole === "supervisor" || userRole === "warehouse";

  // Sum square_meters from lots grouped by material_id
  const lotsByMaterial: Record<string, number> = {};
  for (const lot of materialLots || []) {
    const mid = lot.material_id;
    lotsByMaterial[mid] = (lotsByMaterial[mid] || 0) + (lot.square_meters || 0);
  }

  const stats = {
    total: materials?.length || 0,
    lowStock: materials?.filter((m) => m.current_stock < m.min_stock).length || 0,
    totalValue: materials?.reduce((sum, m) => {
      // Use lots sum if available, fallback to material's square_meters
      const sqMeters = lotsByMaterial[m.id] || m.square_meters || 0;
      return sum + sqMeters * (m.cost_per_unit || 0);
    }, 0) || 0,
    categories: categories.length,
  };

  if (viewMaterialId) {
    const detailsContent = (
      <MaterialDetails
        materialId={viewMaterialId}
        onBack={() => setViewMaterialId(null)}
      />
    );
    return embedded ? detailsContent : (
      <DashboardLayout>
        {detailsContent}
      </DashboardLayout>
    );
  }

  const content = (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Inventario de Materiales
            </h1>
            <p className="text-muted-foreground mt-1">
              Control y gestión de materiales de producción
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* View Toggle */}
            <div className="flex items-center border rounded-lg p-1">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className="gap-1"
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Grid</span>
              </Button>
              <Button
                variant={viewMode === "kanban" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("kanban")}
                className="gap-1"
              >
                <Columns className="w-4 h-4" />
                <span className="hidden sm:inline">Kanban</span>
              </Button>
            </div>
            {canManage && (
              <Button onClick={handleNew}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Material
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Package className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">{stats.lowStock}</p>
                  <p className="text-sm text-muted-foreground">Stock Bajo</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {canSeeCosts && (
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {stats.totalValue.toLocaleString("es-ES", {
                        style: "currency",
                        currency: "EUR",
                      })}
                    </p>
                    <p className="text-sm text-muted-foreground">Valor Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Boxes className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.categories}</p>
                  <p className="text-sm text-muted-foreground">Categorías</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, código o categoría..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat!}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={stockFilter} onValueChange={setStockFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Stock" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo el stock</SelectItem>
              <SelectItem value="low">Stock bajo</SelectItem>
              <SelectItem value="normal">Stock normal</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          </div>
        ) : viewMode === "kanban" ? (
          <MaterialKanbanView
            materials={filteredMaterials || []}
            onSelectMaterial={handleViewDetails}
          />
        ) : filteredMaterials && filteredMaterials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMaterials.map((material) => {
              const isLowStock = material.current_stock < material.min_stock;
              const stockPercentage = material.max_stock
                ? Math.min((material.current_stock / material.max_stock) * 100, 100)
                : null;
              const hasFreezerDetails = material.is_freezer_material && (
                material.roll_count != null ||
                material.batch_number ||
                material.roll_number ||
                material.production_date ||
                material.expiry_date
              );

              return (
                <Card
                  key={material.id}
                  className={cn(
                    "cursor-pointer hover:shadow-lg transition-all",
                    isLowStock && "border-l-4 border-l-yellow-500"
                  )}
                  onClick={() => handleViewDetails(material.id)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            "p-2 rounded-lg",
                            isLowStock ? "bg-yellow-500/10" : "bg-primary/10"
                          )}
                        >
                          <Package
                            className={cn(
                              "w-5 h-5",
                              isLowStock ? "text-yellow-500" : "text-primary"
                            )}
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold">{material.name}</h3>
                          {material.code && (
                            <p className="text-xs text-muted-foreground">
                              {material.code}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!material.is_active && (
                          <Badge variant="secondary" className="text-xs">
                            Inactivo
                          </Badge>
                        )}
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => handleEdit(material, e)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {(material.category || material.is_freezer_material) && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {material.category && (
                          <Badge variant="outline">{material.category}</Badge>
                        )}
                        {material.is_freezer_material && (
                          <Badge variant="secondary">Congelador</Badge>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Stock</span>
                        <span
                          className={cn(
                            "text-sm font-bold",
                            isLowStock ? "text-yellow-600" : "text-green-600"
                          )}
                        >
                          {material.current_stock} {material.unit}
                        </span>
                      </div>

                      {stockPercentage !== null && (
                        <Progress
                          value={stockPercentage}
                          className={cn(
                            "h-2",
                            isLowStock && "[&>div]:bg-yellow-500",
                            !isLowStock && stockPercentage > 50 && "[&>div]:bg-green-500"
                          )}
                        />
                      )}

                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Min: {material.min_stock}</span>
                        {material.max_stock && <span>Max: {material.max_stock}</span>}
                      </div>
                    </div>

                    {material.is_freezer_material && (
                      <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary/70 mb-2">
                          Zona de congeladores
                        </p>
                        {hasFreezerDetails ? (
                          <div className="space-y-1 text-xs">
                            {material.roll_count != null && (
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Bobinas/Rollos:</span>
                                <span className="font-medium">{material.roll_count}</span>
                              </div>
                            )}
                            {material.batch_number && (
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Lote:</span>
                                <span className="font-medium">{material.batch_number}</span>
                              </div>
                            )}
                            {material.roll_number && (
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Rollo:</span>
                                <span className="font-medium">{material.roll_number}</span>
                              </div>
                            )}
                            {material.production_date && (
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Producción:</span>
                                <span className="font-medium">
                                  {new Date(material.production_date).toLocaleDateString("es-ES")}
                                </span>
                              </div>
                            )}
                            {material.expiry_date && (
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Caducidad:</span>
                                <span className="font-medium">
                                  {new Date(material.expiry_date).toLocaleDateString("es-ES")}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">Sin datos de congelador</p>
                        )}
                      </div>
                    )}

                    {isLowStock && (
                      <div className="flex items-center gap-2 mt-3 p-2 rounded-lg bg-yellow-500/10 text-yellow-600">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-xs font-medium">
                          Stock bajo - Reabastecer
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-muted-foreground">
                      {material.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {material.location}
                        </div>
                      )}
                      {canSeeCosts && material.cost_per_unit && (
                        <div className="flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" />
                          {((material.square_meters || 0) * material.cost_per_unit).toLocaleString(
                            "es-ES",
                            { style: "currency", currency: "EUR" }
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-center gap-1 mt-3 text-xs text-muted-foreground">
                      <Eye className="w-3 h-3" />
                      Click para ver detalles
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Package className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-2">
                {searchTerm || categoryFilter !== "all" || stockFilter !== "all"
                  ? "No se encontraron materiales con los filtros seleccionados"
                  : "No hay materiales registrados"}
              </p>
              {canManage && (
                <Button onClick={handleNew} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Primer Material
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <MaterialInventoryFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          material={selectedMaterial}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["material-inventory"] });
          }}
        />
      </div>
    </>
  );

  return embedded ? content : (
    <DashboardLayout>
      {content}
    </DashboardLayout>
  );
}
